import { useEffect, useRef } from "react"
import useDeviceCapability from "hooks/useDeviceCapability"
import usePrefersReducedMotion from "hooks/usePrefersReducedMotion"

function randomNormal() {
  let u = 0
  let v = 0
  while (u === 0) u = Math.random()
  while (v === 0) v = Math.random()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

function finite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback
}

function clamp(value, min, max, fallback = min) {
  const v = finite(value, fallback)
  return Math.max(min, Math.min(max, v))
}

export default function TorusCanvas({
  centerX,
  centerY,
  className = "",
  style = {},
  hue = 168,
  background = "black",
  settingsOverrides = null,
  adaptiveOverrides = null,
}) {
  const canvasRef = useRef(null)
  const centerXRef = useRef(centerX)
  const centerYRef = useRef(centerY)
  const prefersReducedMotion = usePrefersReducedMotion()
  const { saveData, isMobile, lowConcurrency } = useDeviceCapability()
  // Treat saveData like prefers-reduced-motion: skip the animation
  // entirely. The visitor has explicitly asked for less.
  const skipAnimation = prefersReducedMotion || saveData

  // TODO(perf, follow-up): two more deferred optimizations worth a
  // look if the field reports still flag slowness:
  //   1. Connection-quality detection via
  //      navigator.connection.effectiveType ("slow-2g" / "2g" / "3g"
  //      / "4g") on top of the existing saveData / mobile /
  //      hardwareConcurrency hints — could feed into maxCount or
  //      trigger the static fallback on slow connections.
  //   2. Gradient pooling. The strand-draw loop currently allocates
  //      one LinearGradient and one RadialGradient per particle per
  //      frame, which is the loudest remaining per-frame allocation.
  //      Bigger refactor with visual-validation risk since the
  //      gradient endpoints are position-keyed.

  // Keep refs in sync with props — no effect restart needed
  useEffect(() => {
    centerXRef.current = centerX
    centerYRef.current = centerY
  }, [centerX, centerY])

  useEffect(() => {
    if (skipAnimation) return
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Mobile devices tend to have higher devicePixelRatio AND weaker
    // GPUs, so the pixel cost grows faster than the rendering budget.
    // Drop the cap to 1× for coarse-pointer devices.
    const DPR_CAP = isMobile ? 1.0 : 1.25
    const OFFSCREEN_MARGIN = 160
    const GLOW_ALPHA_SKIP_THRESHOLD = 0.05

    // Thresholds for the "skip the strand gradient and stroke with a
    // solid mid-color" fast path. Calibrated so the resulting stroke
    // is visually indistinguishable from a gradient at hero size and
    // motion speed — saturation/lightness are HSL units (0–100),
    // alpha is 0–1.
    const GRADIENT_S_DELTA = 4
    const GRADIENT_L_DELTA = 4
    const GRADIENT_A_DELTA = 0.04

    let width = 0
    let height = 0
    let dpr = 1
    let rafId = 0
    let running = false
    // Combined pause state: only run the rAF loop when the canvas is in
    // the viewport AND the tab is visible. Either condition false → stop.
    let canvasInView = true
    let docVisible = typeof document === "undefined" ? true : !document.hidden

    const adaptive = {
      enabled: true,
      minCount: 2400,
      // Lower the ceiling on machines that report fewer logical cores.
      // Imperfect (Safari clamps to 8, etc.) but useful as a floor —
      // when in doubt, keep the ramp shorter.
      maxCount: lowConcurrency ? 3000 : 5000,
      // step: per-adjustment delta when ramping DOWN (slow machines).
      // stepUp: per-adjustment delta when ramping UP (fast machines).
      // Asymmetric on purpose — start low, ramp up fast on capable
      // hardware; ramp down conservatively if/when a slower machine
      // does need to shed particles.
      step: 400,
      stepUp: 800,
      rampPerFrame: 24,
      targetFpsLow: 42,
      targetFpsHigh: 57,
      sampleFrames: 30,
      cooldownFrames: 120,
      frameCounter: 0,
      sampleAccumMs: 0,
      cooldownRemaining: 0,
      desiredCount: 2400,
      ...(adaptiveOverrides || {}),
    }

    const settings = {
      count: 2400,
      baseCount: 2400,
      majorRadius: 190,
      tubeRadius: 72,
      fuzz: 4,
      pointScale: 3.5,
      bendAmplitude: 2.8,
      lineLightnessBoost: 22,
      dotLightnessBoost: 10,
      dotActivationBoost: 2.0,
      dotMaxAlpha: 0.48,
      dotBaseLightnessBoost: 4,
      nodeDotScale: 0.8,
      rippleDuration: 1.5,
      rippleMaxRadiusScale: 9.0,
      rippleMaxAlpha: 0.65,
      rippleLineWidth: 0.9,
      rippleLineWidthMin: 0.18,
      dataSpeed: 0.16,
      pulseGlowLightnessBoost: 26,
      pulseGlowOpacityBoost: 0.4,
      pulseGlowWidthScale: 3.2,
      pulseGlowLengthScale: 2.7,
      cameraDriftSpeedY: 0.00018,
      cameraDriftSpeedX: 0.00007,
      farDepthSaturationScale: 0.78,
      farDepthLightnessDrop: 7.5,
      dataDotScale: 1.15,
      dataDotLightnessBoost: 18,
      cameraZ: 900,
      focalLength: 800,
      zoom: 1,
      speed: 17.5,
      viewTiltX: 0,
      viewTiltY: 0,
      // Static base tilt that the drift oscillation is added to.
      // Defaults to 0 so the home-page hero keeps its existing motion.
      viewTiltOffsetX: 0,
      viewTiltOffsetY: 0,
      // Minimum alpha for a particle at the back of the torus (depth
      // alpha clamps to this floor). Raising the floor weakens the
      // depth-based transparency falloff so the far side stays visible.
      depthAlphaFloor: 0.08,
      nearTorusOffsetZ: 650,
      nearScale: 3.8,
      nearFitViewport: false,
      nearFitOverscan: 1.0,
      farScale: 1.0,
      farFitViewport: true,
      farFitOverscan: 1.08,
      farTorusOffsetZ: 0,
      nearTorusAlpha: 0.65,
      torusSpinSpeed: 0.0525,
      rippleTriggerChance: 0.22,
      hue,
      ...(settingsOverrides || {}),
    }
    // Keep adaptive count bounds in sync with any settings-override count.
    if (settingsOverrides?.count) {
      adaptive.desiredCount = settingsOverrides.count
    }

    let pointCount = settings.count
    let activeCount = settings.count

    let posX
    let posY
    let posZ
    let targetX
    let targetY
    let targetZ

    let farX
    let farY
    let farR
    let farZ
    let farA
    let farH
    let farS
    let farL

    let nearX
    let nearY
    let nearZ
    let nearA
    let nearH
    let nearS
    let nearL

    let tripStartDistance
    let curvePhase
    let curveFreq
    let dataPhase
    let flashTimer
    let dotActivationTimer
    let rippleTimer
    let prevDataT
    // Per-particle stagger offset (ms) used by the entrance reveal —
    // each particle is invisible until `entranceStart + revealDelay`,
    // then fades to full alpha over REVEAL_FADE_MS.
    let revealDelay
    // Set to performance.now() the first time the rAF loop starts.
    // Stays at 0 until then so the entrance only plays once, even
    // across IntersectionObserver / visibilitychange pause-resume
    // cycles (the timer keeps advancing while the loop is paused, so
    // by the time we resume the entrance window is already over and
    // every particle's revealAlpha clamps to 1).
    let entranceStartTime = 0
    const REVEAL_DELAY_MAX = 1000
    const REVEAL_FADE_MS = 500
    const REVEAL_TOTAL_MS = REVEAL_DELAY_MAX + REVEAL_FADE_MS

    function hsla(h, s, l, alpha, lightnessBoost = 0) {
      const hh = clamp(h, 0, 360, 168)
      const ss = clamp(s, 0, 100, 77)
      const ll = clamp(l + lightnessBoost, 0, 100, 20)
      const aa = clamp(alpha, 0, 1, 0)
      return `hsla(${hh} ${ss}% ${ll}% / ${aa})`
    }

    function resize() {
      const rect = canvas.getBoundingClientRect()
      dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP)
      width = Math.max(1, Math.floor(rect.width))
      height = Math.max(1, Math.floor(rect.height))
      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    function sampleTorusPoint() {
      const R = settings.majorRadius
      const r = settings.tubeRadius
      const fuzz = settings.fuzz
      const u = Math.random() * Math.PI * 2
      const v = Math.random() * Math.PI * 2

      let x = (R + r * Math.cos(v)) * Math.cos(u)
      let y = (R + r * Math.cos(v)) * Math.sin(u)
      let z = r * Math.sin(v)

      if (fuzz > 0) {
        x += randomNormal() * fuzz
        y += randomNormal() * fuzz
        z += randomNormal() * fuzz
      }

      return [x, y, z]
    }

    function allocateBuffers() {
      farX = new Float32Array(pointCount)
      farY = new Float32Array(pointCount)
      farR = new Float32Array(pointCount)
      farZ = new Float32Array(pointCount)
      farA = new Float32Array(pointCount)
      farH = new Float32Array(pointCount)
      farS = new Float32Array(pointCount)
      farL = new Float32Array(pointCount)

      nearX = new Float32Array(pointCount)
      nearY = new Float32Array(pointCount)
      nearZ = new Float32Array(pointCount)
      nearA = new Float32Array(pointCount)
      nearH = new Float32Array(pointCount)
      nearS = new Float32Array(pointCount)
      nearL = new Float32Array(pointCount)

      tripStartDistance = new Float32Array(pointCount)
      curvePhase = new Float32Array(pointCount)
      curveFreq = new Float32Array(pointCount)
      dataPhase = new Float32Array(pointCount)
      flashTimer = new Float32Array(pointCount)
      dotActivationTimer = new Float32Array(pointCount)
      rippleTimer = new Float32Array(pointCount)
      prevDataT = new Float32Array(pointCount)
      revealDelay = new Float32Array(pointCount)
    }

    function seedDynamics(i, sx, sy, sz, dx, dy, dz) {
      tripStartDistance[i] = Math.max(
        0.0001,
        Math.hypot(dx - sx, dy - sy, dz - sz)
      )
      curvePhase[i] = Math.random() * Math.PI * 2
      curveFreq[i] = 1.6 + Math.random() * 1.8
      dataPhase[i] = Math.random()
      prevDataT[i] = dataPhase[i]
      flashTimer[i] = 0
      dotActivationTimer[i] = 0
      rippleTimer[i] = 0
      // Stagger the initial reveal across REVEAL_DELAY_MAX ms. If the
      // entrance has already played out (e.g. the adaptive sampler is
      // ramping new particles in mid-session), set delay to 0 so they
      // appear instantly — the per-frame guard short-circuits anyway
      // once REVEAL_TOTAL_MS has elapsed, but this keeps the buffer
      // value coherent.
      revealDelay[i] =
        entranceStartTime === 0 ? Math.random() * REVEAL_DELAY_MAX : 0
    }

    function appendPoints(startIndex, endIndex) {
      for (let i = startIndex; i < endIndex; i++) {
        const start = sampleTorusPoint()
        const dest = sampleTorusPoint()
        posX[i] = start[0]
        posY[i] = start[1]
        posZ[i] = start[2]
        targetX[i] = dest[0]
        targetY[i] = dest[1]
        targetZ[i] = dest[2]
        seedDynamics(i, start[0], start[1], start[2], dest[0], dest[1], dest[2])
      }
    }

    function generateTorusPoints() {
      settings.count = Math.round(
        clamp(
          settings.count,
          adaptive.minCount,
          adaptive.maxCount,
          settings.baseCount
        )
      )
      pointCount = settings.count
      activeCount = settings.count
      adaptive.desiredCount = settings.count
      posX = new Float32Array(pointCount)
      posY = new Float32Array(pointCount)
      posZ = new Float32Array(pointCount)
      targetX = new Float32Array(pointCount)
      targetY = new Float32Array(pointCount)
      targetZ = new Float32Array(pointCount)
      allocateBuffers()
      appendPoints(0, pointCount)
    }

    function assignNewTarget(i) {
      const dest = sampleTorusPoint()
      targetX[i] = dest[0]
      targetY[i] = dest[1]
      targetZ[i] = dest[2]
      tripStartDistance[i] = Math.max(
        0.0001,
        Math.hypot(dest[0] - posX[i], dest[1] - posY[i], dest[2] - posZ[i])
      )
      curvePhase[i] = (Math.random() - 0.5) * 0.8
      curveFreq[i] = 1.6 + Math.random() * 1.8
    }

    function projectInto(x0, y0, z0, extraZ, sinX, cosX, sinY, cosY, out) {
      const xz = z0 + extraZ
      const x1 = x0 * cosY + xz * sinY
      const z1 = -x0 * sinY + xz * cosY
      const y2 = y0 * cosX - z1 * sinX
      const z2 = y0 * sinX + z1 * cosX
      const depth = settings.cameraZ - z2
      const safeDepth = Math.max(1e-6, depth)
      const scale = settings.focalLength / safeDepth
      out[0] = x1
      out[1] = y2
      out[2] = z2
      out[3] = scale
    }

    // Only log adaptive tuning in local dev + Netlify deploy previews.
    // netlify.toml sets GATSBY_IS_PREVIEW=true on the deploy-preview
    // context, and NODE_ENV is "development" under `gatsby develop`.
    const isDebugBuild =
      process.env.NODE_ENV === "development" ||
      process.env.GATSBY_IS_PREVIEW === "true"

    function logAdaptiveChange(reason, previousCount, nextCount) {
      if (previousCount === nextCount) return
      if (!isDebugBuild) return
      console.info(
        `[lattice][adaptive] ${reason}: ${previousCount.toLocaleString()} -> ${nextCount.toLocaleString()} nodes`
      )
    }

    function setPointCount(newCount) {
      const clamped = Math.round(
        clamp(
          newCount,
          adaptive.minCount,
          adaptive.maxCount,
          settings.baseCount
        )
      )
      if (clamped === adaptive.desiredCount) return
      const previousDesired = adaptive.desiredCount
      adaptive.desiredCount = clamped
      logAdaptiveChange("target", previousDesired, clamped)
      adaptive.cooldownRemaining = adaptive.cooldownFrames
      adaptive.frameCounter = 0
      adaptive.sampleAccumMs = 0
    }

    function resizeCapacity(newCapacity) {
      const oldCount = pointCount
      const oldPosX = posX
      const oldPosY = posY
      const oldPosZ = posZ
      const oldTargetX = targetX
      const oldTargetY = targetY
      const oldTargetZ = targetZ
      const oldTripStartDistance = tripStartDistance
      const oldCurvePhase = curvePhase
      const oldCurveFreq = curveFreq
      const oldDataPhase = dataPhase
      const oldFlashTimer = flashTimer
      const oldDotActivationTimer = dotActivationTimer
      const oldRippleTimer = rippleTimer
      const oldPrevDataT = prevDataT
      const oldRevealDelay = revealDelay

      pointCount = newCapacity
      posX = new Float32Array(pointCount)
      posY = new Float32Array(pointCount)
      posZ = new Float32Array(pointCount)
      targetX = new Float32Array(pointCount)
      targetY = new Float32Array(pointCount)
      targetZ = new Float32Array(pointCount)
      allocateBuffers()

      const copyCount = Math.min(oldCount, pointCount, activeCount)
      posX.set(oldPosX.subarray(0, copyCount))
      posY.set(oldPosY.subarray(0, copyCount))
      posZ.set(oldPosZ.subarray(0, copyCount))
      targetX.set(oldTargetX.subarray(0, copyCount))
      targetY.set(oldTargetY.subarray(0, copyCount))
      targetZ.set(oldTargetZ.subarray(0, copyCount))
      tripStartDistance.set(oldTripStartDistance.subarray(0, copyCount))
      curvePhase.set(oldCurvePhase.subarray(0, copyCount))
      curveFreq.set(oldCurveFreq.subarray(0, copyCount))
      dataPhase.set(oldDataPhase.subarray(0, copyCount))
      flashTimer.set(oldFlashTimer.subarray(0, copyCount))
      dotActivationTimer.set(oldDotActivationTimer.subarray(0, copyCount))
      rippleTimer.set(oldRippleTimer.subarray(0, copyCount))
      prevDataT.set(oldPrevDataT.subarray(0, copyCount))
      revealDelay.set(oldRevealDelay.subarray(0, copyCount))
      activeCount = copyCount
      settings.count = pointCount
    }

    function rampActiveCount() {
      const desired = adaptive.desiredCount
      if (desired > pointCount) {
        const newCapacity = Math.min(
          adaptive.maxCount,
          Math.max(desired, pointCount + adaptive.step)
        )
        logAdaptiveChange("capacity+", pointCount, newCapacity)
        resizeCapacity(newCapacity)
      }

      if (activeCount < desired) {
        const nextCount = Math.min(desired, activeCount + adaptive.rampPerFrame)
        appendPoints(activeCount, nextCount)
        activeCount = nextCount
      } else if (activeCount > desired) {
        activeCount = Math.max(desired, activeCount - adaptive.rampPerFrame)
      }

      if (desired < pointCount && activeCount === desired) {
        logAdaptiveChange("capacity-", pointCount, desired)
        resizeCapacity(desired)
      }
    }

    function updateAdaptiveQuality(frameMs) {
      if (!adaptive.enabled) return
      if (adaptive.cooldownRemaining > 0) {
        adaptive.cooldownRemaining -= 1
        return
      }

      adaptive.frameCounter += 1
      adaptive.sampleAccumMs += frameMs
      if (adaptive.frameCounter < adaptive.sampleFrames) return

      const avgMs = adaptive.sampleAccumMs / adaptive.sampleFrames
      const fps = 1000 / Math.max(1e-6, avgMs)
      adaptive.frameCounter = 0
      adaptive.sampleAccumMs = 0

      if (fps < adaptive.targetFpsLow && settings.count > adaptive.minCount) {
        setPointCount(settings.count - adaptive.step)
      } else if (
        fps > adaptive.targetFpsHigh &&
        settings.count < adaptive.maxCount
      ) {
        setPointCount(settings.count + adaptive.stepUp)
      }
    }

    resize()
    generateTorusPoints()

    const projFar = new Float32Array(4)
    const projNear = new Float32Array(4)
    let lastTime = performance.now()

    function frame(now) {
      const rawDt = now - lastTime
      const dt = Math.min(50, rawDt)
      lastTime = now
      updateAdaptiveQuality(rawDt)
      rampActiveCount()
      const dtSeconds = dt / 1000
      const time = now * 0.001

      settings.viewTiltY =
        settings.viewTiltOffsetY +
        Math.sin(time * settings.cameraDriftSpeedY * 60) * 0.06
      settings.viewTiltX =
        settings.viewTiltOffsetX +
        Math.sin(time * settings.cameraDriftSpeedX * 60) * 0.025

      const sinTiltX = Math.sin(settings.viewTiltX)
      const cosTiltX = Math.cos(settings.viewTiltX)
      const sinTiltY = Math.sin(settings.viewTiltY)
      const cosTiltY = Math.cos(settings.viewTiltY)

      const step = settings.speed * dtSeconds
      const spin = time * settings.torusSpinSpeed
      const cosSpin = Math.cos(spin)
      const sinSpin = Math.sin(spin)

      const cx =
        typeof centerXRef.current === "number"
          ? centerXRef.current
          : width * 0.5
      const cy =
        typeof centerYRef.current === "number"
          ? centerYRef.current
          : height * 0.5

      const nearDepthAtCenter = Math.max(
        1e-6,
        settings.cameraZ - settings.nearTorusOffsetZ
      )
      const nearCenterScale = settings.focalLength / nearDepthAtCenter
      const autoNearScale = settings.nearFitViewport
        ? (height * 0.5 * settings.nearFitOverscan) /
          Math.max(1e-6, settings.majorRadius * nearCenterScale)
        : settings.nearScale
      const effectiveNearScale = clamp(
        settings.nearFitViewport ? autoNearScale : settings.nearScale,
        0.01,
        100,
        settings.nearScale
      )

      const farDepthAtCenter = Math.max(
        1e-6,
        settings.cameraZ - settings.farTorusOffsetZ
      )
      const farCenterScale = settings.focalLength / farDepthAtCenter
      const autoFarScale = settings.farFitViewport
        ? (height * 0.5 * settings.farFitOverscan) /
          Math.max(1e-6, settings.majorRadius * farCenterScale)
        : settings.farScale
      const effectiveFarScale = clamp(
        settings.farFitViewport ? autoFarScale : settings.farScale,
        0.01,
        100,
        settings.farScale
      )

      const holeRadius = settings.majorRadius - settings.tubeRadius * 0.9
      const fadeWidth = settings.tubeRadius * 0.8
      const nearZMax =
        settings.nearTorusOffsetZ + settings.tubeRadius * effectiveNearScale
      const farZMin = -(
        settings.tubeRadius * effectiveFarScale +
        settings.farTorusOffsetZ +
        40
      )
      const depthDenom = Math.max(1e-6, nearZMax - farZMin)

      // Staggered entrance: each particle's far/near alpha is faded
      // from 0→1 over REVEAL_FADE_MS, starting at its
      // per-particle revealDelay offset from entranceStartTime. Once
      // every particle has finished fading in (entranceElapsed >
      // REVEAL_TOTAL_MS), the per-particle math is skipped entirely.
      const entranceElapsed = now - entranceStartTime
      const entranceActive = entranceElapsed < REVEAL_TOTAL_MS

      for (let i = 0; i < activeCount; i++) {
        if (flashTimer[i] > 0)
          flashTimer[i] = Math.max(0, flashTimer[i] - dtSeconds)
        if (dotActivationTimer[i] > 0)
          dotActivationTimer[i] = Math.max(0, dotActivationTimer[i] - dtSeconds)
        if (rippleTimer[i] > 0)
          rippleTimer[i] = Math.max(0, rippleTimer[i] - dtSeconds)

        const dx = targetX[i] - posX[i]
        const dy = targetY[i] - posY[i]
        const dz = targetZ[i] - posZ[i]
        const distance = Math.hypot(dx, dy, dz)

        if (distance <= step || distance < 0.0001) {
          posX[i] = targetX[i]
          posY[i] = targetY[i]
          posZ[i] = targetZ[i]
          assignNewTarget(i)
        } else {
          const inv = step / distance
          posX[i] += dx * inv
          posY[i] += dy * inv
          posZ[i] += dz * inv
        }

        const baseX = posX[i] * settings.zoom
        const baseY = posY[i] * settings.zoom
        const baseZ = posZ[i] * settings.zoom
        const rotX = baseX * cosSpin - baseY * sinSpin
        const rotY = baseX * sinSpin + baseY * cosSpin
        const rotZ = baseZ

        const farModelX = rotX * effectiveFarScale
        const farModelY = rotY * effectiveFarScale
        const farModelZ = rotZ * effectiveFarScale
        const nearModelX = rotX * effectiveNearScale
        const nearModelY = rotY * effectiveNearScale
        const nearModelZ = rotZ * effectiveNearScale

        const scaledHoleRadius = holeRadius * effectiveFarScale
        const scaledFadeWidth = Math.max(1e-6, fadeWidth * effectiveFarScale)
        let holeFade =
          (Math.hypot(farModelX, farModelY) - scaledHoleRadius) /
          scaledFadeWidth
        holeFade = clamp(holeFade, 0, 1, 0)

        projectInto(
          farModelX,
          farModelY,
          farModelZ,
          settings.farTorusOffsetZ,
          sinTiltX,
          cosTiltX,
          sinTiltY,
          cosTiltY,
          projFar
        )
        const farScale = projFar[3]
        farX[i] = cx + projFar[0] * farScale
        farY[i] = cy + projFar[1] * farScale
        farR[i] = Math.min(
          3,
          Math.max(0, farScale * settings.pointScale * holeFade)
        )
        farZ[i] = projFar[2]
        farA[i] = clamp(
          farScale * 0.95,
          settings.depthAlphaFloor,
          1,
          settings.depthAlphaFloor
        )

        let tFar = (projFar[2] - farZMin) / depthDenom
        tFar = clamp(tFar, 0, 1, 0)
        const farDepthFalloff = 1 - tFar
        farH[i] = settings.hue
        farS[i] = clamp(
          77 - farDepthFalloff * (77 * (1 - settings.farDepthSaturationScale)),
          0,
          100,
          77
        )
        farL[i] = clamp(
          6 + 40 * tFar - farDepthFalloff * settings.farDepthLightnessDrop,
          0,
          100,
          20
        )

        projectInto(
          nearModelX,
          nearModelY,
          nearModelZ,
          settings.nearTorusOffsetZ,
          sinTiltX,
          cosTiltX,
          sinTiltY,
          cosTiltY,
          projNear
        )
        const nearScale = projNear[3]
        nearX[i] = cx + projNear[0] * nearScale
        nearY[i] = cy + projNear[1] * nearScale
        nearZ[i] = projNear[2]
        nearA[i] = clamp(
          clamp(nearScale * 0.8, 0.04, 1, 0.04) *
            holeFade *
            settings.nearTorusAlpha,
          0,
          1,
          0
        )

        let tNear = (projNear[2] - farZMin) / depthDenom
        tNear = clamp(tNear, 0, 1, 0)
        const nearDepthFalloff = 1 - tNear
        nearH[i] = settings.hue
        nearS[i] = clamp(
          77 - nearDepthFalloff * (77 * (1 - settings.farDepthSaturationScale)),
          0,
          100,
          77
        )
        nearL[i] = clamp(
          6 + 40 * tNear - nearDepthFalloff * settings.farDepthLightnessDrop,
          0,
          100,
          20
        )

        // Staggered entrance reveal: dial down both alphas while this
        // particle is still inside its fade-in window. The strand-draw
        // loop below derives lineA0/lineA1 from these and the dot
        // loops read farA directly, so the single multiplication here
        // attenuates everything (strand stroke, pulse glow, node dot,
        // ripple) consistently.
        if (entranceActive) {
          const partElapsed = entranceElapsed - revealDelay[i]
          const revealAlpha =
            partElapsed <= 0 ? 0 : Math.min(1, partElapsed / REVEAL_FADE_MS)
          farA[i] *= revealAlpha
          nearA[i] *= revealAlpha
        }
      }

      ctx.clearRect(0, 0, width, height)
      ctx.globalCompositeOperation = "screen"

      for (let i = 0; i < activeCount; i++) {
        if (farR[i] <= 0.001) continue
        const a0 = farA[i]
        const a1 = nearA[i]
        if (a0 <= 0.001 && a1 <= 0.001) continue

        const x0 = farX[i]
        const y0 = farY[i]
        const x1 = nearX[i]
        const y1 = nearY[i]

        if (
          (x0 < -OFFSCREEN_MARGIN && x1 < -OFFSCREEN_MARGIN) ||
          (x0 > width + OFFSCREEN_MARGIN && x1 > width + OFFSCREEN_MARGIN) ||
          (y0 < -OFFSCREEN_MARGIN && y1 < -OFFSCREEN_MARGIN) ||
          (y0 > height + OFFSCREEN_MARGIN && y1 > height + OFFSCREEN_MARGIN)
        )
          continue

        const dx = x1 - x0
        const dy = y1 - y0
        const lineLen = Math.hypot(dx, dy) || 1
        const invLineLen = 1 / lineLen
        const mx = (x0 + x1) * 0.5
        const my = (y0 + y1) * 0.5
        const px = -dy * invLineLen
        const py = dx * invLineLen
        const tx = dx * invLineLen
        const ty = dy * invLineLen

        const remaining = Math.hypot(
          targetX[i] - posX[i],
          targetY[i] - posY[i],
          targetZ[i] - posZ[i]
        )
        const progress =
          tripStartDistance[i] > 0 ? remaining / tripStartDistance[i] : 0
        const clampedProgress = clamp(progress, 0, 1, 0)
        const envelope = clampedProgress * clampedProgress
        const baseBend =
          settings.bendAmplitude *
          Math.min(lineLen * 0.16, tripStartDistance[i] * 0.14)
        const bend =
          baseBend * envelope * Math.sin(time * curveFreq[i] + curvePhase[i])
        const bendMag =
          baseBend > 0 ? clamp(Math.abs(bend) / baseBend, 0, 1, 0) : 0
        const lineLightBoost = bendMag * settings.lineLightnessBoost

        const lineA0 = clamp(a0 * (0.35 + bendMag * 0.85) * 0.335, 0, 1, 0)
        const lineA1 = clamp(a1 * (0.35 + bendMag * 0.85) * 0.335, 0, 1, 0)
        const cxCurve = mx + px * bend
        const cyCurve = my + py * bend
        const dataT = clamp(
          1 - ((time * settings.dataSpeed + dataPhase[i]) % 1),
          0,
          1,
          0
        )

        const lineWidth = Math.min(3, Math.max(0, farR[i] * 2)) * 0.75
        if (lineWidth <= 0.001) continue

        // Strand fill: most strands have a small enough far→near color
        // delta that a solid mid-color stroke is visually
        // indistinguishable from the gradient. When that's the case,
        // skip the gradient allocation entirely (createLinearGradient +
        // 2 addColorStops per strand was the loudest remaining
        // per-frame allocation in the hot path). For strands where the
        // delta is meaningful — typically those spanning a large depth
        // range — fall back to the original gradient.
        const dS = farS[i] - nearS[i]
        const dL = farL[i] - nearL[i]
        const dA = lineA0 - lineA1
        const smallDelta =
          dS > -GRADIENT_S_DELTA &&
          dS < GRADIENT_S_DELTA &&
          dL > -GRADIENT_L_DELTA &&
          dL < GRADIENT_L_DELTA &&
          dA > -GRADIENT_A_DELTA &&
          dA < GRADIENT_A_DELTA

        let strandStyle
        if (smallDelta) {
          const midS = (farS[i] + nearS[i]) * 0.5
          const midL = (farL[i] + nearL[i]) * 0.5
          const midA = (lineA0 + lineA1) * 0.5
          strandStyle = hsla(farH[i], midS, midL, midA, lineLightBoost)
        } else {
          const strandGradient = ctx.createLinearGradient(x0, y0, x1, y1)
          strandGradient.addColorStop(
            0,
            hsla(farH[i], farS[i], farL[i], lineA0, lineLightBoost)
          )
          strandGradient.addColorStop(
            1,
            hsla(nearH[i], nearS[i], nearL[i], lineA1, lineLightBoost)
          )
          strandStyle = strandGradient
        }

        ctx.beginPath()
        ctx.moveTo(x0, y0)
        ctx.quadraticCurveTo(cxCurve, cyCurve, x1, y1)
        ctx.strokeStyle = strandStyle
        ctx.lineWidth = lineWidth
        ctx.stroke()

        const omt = 1 - dataT
        const pulseX =
          omt * omt * x0 + 2 * omt * dataT * cxCurve + dataT * dataT * x1
        const pulseY =
          omt * omt * y0 + 2 * omt * dataT * cyCurve + dataT * dataT * y1

        if (lineA0 + lineA1 > GLOW_ALPHA_SKIP_THRESHOLD) {
          const pulseRadius = Math.max(
            4,
            lineWidth * settings.pulseGlowWidthScale
          )
          const glowIsFar = dataT < 0.5
          const glowH = glowIsFar ? farH[i] : nearH[i]
          const glowS = glowIsFar ? farS[i] : nearS[i]
          const glowL = glowIsFar ? farL[i] : nearL[i]
          const glowAlpha = clamp(
            Math.max(lineA0, lineA1) + settings.pulseGlowOpacityBoost,
            0,
            1,
            0.4
          )

          ctx.save()
          ctx.translate(pulseX, pulseY)
          ctx.rotate(Math.atan2(ty, tx))
          ctx.scale(settings.pulseGlowLengthScale, 1)
          const glowGradient = ctx.createRadialGradient(
            0,
            0,
            0,
            0,
            0,
            pulseRadius
          )
          glowGradient.addColorStop(
            0,
            hsla(
              glowH,
              glowS,
              glowL,
              glowAlpha,
              lineLightBoost + settings.pulseGlowLightnessBoost
            )
          )
          glowGradient.addColorStop(
            1,
            hsla(glowH, glowS, glowL, 0, lineLightBoost)
          )
          ctx.beginPath()
          ctx.arc(0, 0, pulseRadius, 0, Math.PI * 2)
          ctx.fillStyle = glowGradient
          ctx.fill()
          ctx.restore()
        }

        if (dataT > prevDataT[i]) {
          flashTimer[i] = 0.22
          dotActivationTimer[i] = 0.55
          if (Math.random() < settings.rippleTriggerChance) {
            rippleTimer[i] = settings.rippleDuration
          }
        }
        prevDataT[i] = dataT

        const dataRadius =
          Math.min(3, Math.max(0.7, farR[i] * settings.dataDotScale)) * 0.5
        const dataAlpha = clamp(
          Math.min(1, 0.35 + a0 * 0.55 + a1 * 0.2 + bendMag * 0.2) * 0.25,
          0,
          1,
          0
        )
        const dataIsFar = dataT < 0.5
        const dataH = dataIsFar ? farH[i] : nearH[i]
        const dataS = dataIsFar ? farS[i] : nearS[i]
        const dataL = dataIsFar ? farL[i] : nearL[i]

        if (
          pulseX >= -OFFSCREEN_MARGIN &&
          pulseX <= width + OFFSCREEN_MARGIN &&
          pulseY >= -OFFSCREEN_MARGIN &&
          pulseY <= height + OFFSCREEN_MARGIN
        ) {
          ctx.beginPath()
          ctx.arc(pulseX, pulseY, dataRadius, 0, Math.PI * 2)
          ctx.fillStyle = hsla(
            dataH,
            dataS,
            dataL,
            dataAlpha,
            lineLightBoost + settings.dataDotLightnessBoost * 0.5
          )
          ctx.fill()
        }
      }

      for (let i = 0; i < activeCount; i++) {
        if (farA[i] <= 0.001 || farR[i] <= 0.001) continue
        const x = farX[i]
        const y = farY[i]
        if (
          x < -OFFSCREEN_MARGIN ||
          x > width + OFFSCREEN_MARGIN ||
          y < -OFFSCREEN_MARGIN ||
          y > height + OFFSCREEN_MARGIN
        )
          continue

        const dotAlpha = clamp(
          settings.dotMaxAlpha + farA[i] * 0.18,
          0,
          1,
          settings.dotMaxAlpha
        )
        const activeBoost = rippleTimer[i] > 0 ? settings.dotActivationBoost : 1
        const dotLightBoost =
          settings.dotBaseLightnessBoost +
          settings.dotLightnessBoost * activeBoost

        ctx.beginPath()
        ctx.arc(x, y, farR[i] * settings.nodeDotScale, 0, Math.PI * 2)
        ctx.fillStyle = hsla(farH[i], farS[i], farL[i], dotAlpha, dotLightBoost)
        ctx.fill()

        const rippleProgress = clamp(
          1 - rippleTimer[i] / Math.max(1e-6, settings.rippleDuration),
          0,
          1,
          0
        )
        if (rippleTimer[i] > 0 && rippleProgress >= 0 && rippleProgress <= 1) {
          const eased = 1 - (1 - rippleProgress) ** 1.6
          const rippleRadius =
            farR[i] + farR[i] * settings.rippleMaxRadiusScale * eased
          const rippleAlpha = clamp(
            settings.rippleMaxAlpha * (1 - rippleProgress),
            0,
            1,
            0
          )
          if (rippleAlpha >= 0.03) {
            const rippleLightBoost =
              settings.dotLightnessBoost + 22 * (1 - rippleProgress * 0.35)
            const rippleWidth =
              settings.rippleLineWidth -
              (settings.rippleLineWidth - settings.rippleLineWidthMin) * eased

            ctx.save()
            ctx.setLineDash([4, 6])
            ctx.beginPath()
            ctx.arc(x, y, rippleRadius, 0, Math.PI * 2)
            ctx.strokeStyle = hsla(
              farH[i],
              farS[i],
              farL[i],
              rippleAlpha,
              rippleLightBoost + 10
            )
            ctx.lineWidth = rippleWidth
            ctx.stroke()
            ctx.restore()
          }
        }
      }

      if (running) {
        rafId = window.requestAnimationFrame(frame)
      } else {
        rafId = 0
      }
    }

    function startLoop() {
      if (running) return
      running = true
      // Reset lastTime so the resumed frame doesn't see a giant dt that
      // would tank the adaptive sampler with a single bad reading.
      lastTime = performance.now()
      // First-ever start anchors the staggered entrance reveal. We
      // intentionally do NOT reset this on subsequent resumes (after a
      // visibility-pause or scroll-out), so the reveal only plays once
      // per page load — the elapsed-time check in the projection loop
      // will be past REVEAL_TOTAL_MS by the time we resume and short-
      // circuit to revealAlpha = 1.
      if (entranceStartTime === 0) entranceStartTime = lastTime
      rafId = window.requestAnimationFrame(frame)
    }

    function stopLoop() {
      if (!running) return
      running = false
      if (rafId) {
        window.cancelAnimationFrame(rafId)
        rafId = 0
      }
    }

    function syncRunState() {
      if (canvasInView && docVisible) startLoop()
      else stopLoop()
    }

    let resizeTimer = null
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(resize, 150)
    })
    const resizeTarget = canvas.parentElement || canvas
    resizeObserver.observe(resizeTarget)

    // Pause when the canvas scrolls out of the viewport. TorusCanvas only
    // mounts inside hero sections (home + AI hub/spokes via
    // AmbientTorusCanvas) — once the visitor scrolls past it there's no
    // reason to keep doing thousands of particles' worth of math per frame.
    const intersectionObserver =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            ([entry]) => {
              canvasInView = entry.isIntersecting
              syncRunState()
            },
            { threshold: 0 }
          )
        : null
    if (intersectionObserver) intersectionObserver.observe(canvas)

    // Pause on tab hide. Browsers throttle rAF in background tabs already,
    // but throttle ≠ stop, and on lower-end machines the residual cost is
    // still meaningful next to the rest of the tab's work.
    const handleVisibilityChange = () => {
      docVisible = !document.hidden
      syncRunState()
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange)
    }

    startLoop()

    return () => {
      stopLoop()
      resizeObserver.disconnect()
      if (intersectionObserver) intersectionObserver.disconnect()
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange)
      }
      if (resizeTimer) clearTimeout(resizeTimer)
    }
  }, [skipAnimation, isMobile, lowConcurrency])

  if (skipAnimation) {
    return (
      <div
        aria-hidden="true"
        className={className}
        style={{
          width: "100%",
          height: "100%",
          background,
          ...style,
        }}
      />
    )
  }

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        background,
        ...style,
      }}
    />
  )
}
